import React, { useState, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useTranslation } from 'react-i18next';
import { apiRequest } from '@/lib/queryClient';
import { useToast } from '@/hooks/use-toast';

const reminderSchema = z.object({
  title: z.string().min(1, 'Title is required').max(200, 'Title too long'),
  description: z.string().optional(),
  date: z.string().min(1, 'Date is required'),
  time: z.string().min(1, 'Time is required'),
  timezone: z.string().default('Europe/Bratislava'),
  offsets: z.array(z.number()).min(1, 'At least one reminder is required'),
  channels: z.array(z.enum(['in_app', 'email'])).min(1, 'At least one channel is required'),
});

type ReminderFormData = z.infer<typeof reminderSchema>;

interface QuickReminderFormProps {
  onSubmit: () => void;
  onCancel: () => void;
  defaultValues?: Partial<ReminderFormData> & { id?: number };
  isEdit?: boolean;
}

const PRESET_OFFSETS = [
  { key: '1h', value: 60 },
  { key: '1d', value: 1440 },
  { key: '2d', value: 2880 },
  { key: '1w', value: 10080 },
];

export default function QuickReminderForm({ onSubmit, onCancel, defaultValues, isEdit = false }: QuickReminderFormProps) {
  const { t } = useTranslation(['reminders', 'common']);
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [customOffset, setCustomOffset] = useState<number | null>(null);

  // Parse default values if provided (for edit)
  const parsedDefaults = useMemo(() => {
    if (!defaultValues) {
      return {
        title: '',
        description: '',
        date: new Date().toISOString().split('T')[0],
        time: new Date().toLocaleTimeString('en-GB', { hour12: false }).slice(0, 5),
        timezone: 'Europe/Bratislava',
        offsets: [60],
        channels: ['in_app'],
      };
    }
    
    // Parse dueAt to date and time
    let date = defaultValues.date;
    let time = defaultValues.time;
    
    if (defaultValues.dueAt && !date && !time) {
      const dueDate = new Date(defaultValues.dueAt);
      date = dueDate.toISOString().split('T')[0];
      time = dueDate.toLocaleTimeString('en-GB', { hour12: false }).slice(0, 5);
    }
    
    return {
      title: defaultValues.title || '',
      description: defaultValues.description || '',
      date: date || new Date().toISOString().split('T')[0],
      time: time || new Date().toLocaleTimeString('en-GB', { hour12: false }).slice(0, 5),
      timezone: defaultValues.timezone || 'Europe/Bratislava',
      offsets: defaultValues.offsets || [60],
      channels: defaultValues.channels || ['in_app'],
    };
  }, [defaultValues]);

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<ReminderFormData>({
    resolver: zodResolver(reminderSchema),
    defaultValues: parsedDefaults,
  });

  const watchedOffsets = watch('offsets');
  const watchedChannels = watch('channels');

  // Handle preset offset selection
  const handlePresetOffset = (offset: number, checked: boolean) => {
    const currentOffsets = watchedOffsets || [];
    if (checked) {
      setValue('offsets', [...currentOffsets, offset]);
    } else {
      setValue('offsets', currentOffsets.filter(o => o !== offset));
    }
  };

  // Handle custom offset
  const handleCustomOffset = () => {
    if (customOffset && customOffset > 0) {
      const currentOffsets = watchedOffsets || [];
      if (!currentOffsets.includes(customOffset)) {
        setValue('offsets', [...currentOffsets, customOffset]);
        setCustomOffset(null);
      }
    }
  };

  // Handle channel selection
  const handleChannelChange = (channel: 'in_app' | 'email', checked: boolean) => {
    const currentChannels = watchedChannels || [];
    if (checked) {
      setValue('channels', [...currentChannels, channel]);
    } else {
      setValue('channels', currentChannels.filter(c => c !== channel));
    }
  };

  // Format offset for display
  const formatOffset = (minutes: number): string => {
    if (minutes < 60) {
      return `${minutes} ${t('form.fields.minutes')}`;
    } else if (minutes < 1440) {
      const hours = Math.floor(minutes / 60);
      return `${hours}h`;
    } else {
      const days = Math.floor(minutes / 1440);
      return `${days}d`;
    }
  };

  const onFormSubmit = async (data: ReminderFormData) => {
    setIsSubmitting(true);
    
    try {
      // Combine date and time
      const dueAt = new Date(`${data.date}T${data.time}:00`).toISOString();
      
      const payload = {
        title: data.title,
        description: data.description,
        dueAt,
        timezone: data.timezone,
        offsets: data.offsets,
        channels: data.channels,
      };

      if (isEdit && defaultValues?.id) {
        // Update existing reminder
        await apiRequest('PATCH', `/api/reminders/${defaultValues.id}`, payload);
      } else {
        // Create new reminder
        await apiRequest('POST', '/api/reminders', payload);
      }
      
      toast({
        title: t('reminders:form.successTitle'),
        description: isEdit ? t('reminders:form.updateSuccess') : t('reminders:form.success')
      });
      onSubmit();
    } catch (error: any) {
      console.error(`Error ${isEdit ? 'updating' : 'creating'} reminder:`, error);
      toast({
        title: t('reminders:form.errorTitle'),
        description: error.message || t('reminders:form.error'),
        variant: 'destructive'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit(onFormSubmit)} className="space-y-4">
      {/* Title */}
      <div className="space-y-2">
        <Label htmlFor="title">{t('form.fields.title')}</Label>
        <Input
          id="title"
          {...register('title')}
          placeholder={t('form.placeholders.title')}
        />
        {errors.title && (
          <p className="text-sm text-destructive">{errors.title.message}</p>
        )}
      </div>

      {/* Description */}
      <div className="space-y-2">
        <Label htmlFor="description">{t('form.fields.description')}</Label>
        <Textarea
          id="description"
          {...register('description')}
          placeholder={t('form.placeholders.description')}
          rows={3}
        />
      </div>

      {/* Date and Time */}
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="date">{t('form.fields.date')}</Label>
          <Input
            id="date"
            type="date"
            {...register('date')}
          />
          {errors.date && (
            <p className="text-sm text-destructive">{errors.date.message}</p>
          )}
        </div>
        
        <div className="space-y-2">
          <Label htmlFor="time">{t('form.fields.time')}</Label>
          <Input
            id="time"
            type="time"
            {...register('time')}
          />
          {errors.time && (
            <p className="text-sm text-destructive">{errors.time.message}</p>
          )}
        </div>
      </div>

      {/* Reminder Presets */}
      <div className="space-y-2">
        <Label>{t('form.fields.reminders')}</Label>
        <div className="grid grid-cols-2 gap-2">
          {PRESET_OFFSETS.map((preset) => (
            <div key={preset.value} className="flex items-center space-x-2">
              <Checkbox
                id={`offset-${preset.value}`}
                checked={watchedOffsets?.includes(preset.value) || false}
                onCheckedChange={(checked) => handlePresetOffset(preset.value, checked as boolean)}
              />
              <Label htmlFor={`offset-${preset.value}`} className="text-sm">
                {t(`form.presets.${preset.key}`)}
              </Label>
            </div>
          ))}
        </div>
        
        {/* Custom offset */}
        <div className="flex items-center space-x-2">
          <Input
            type="number"
            placeholder={t('form.placeholders.minutes')}
            value={customOffset || ''}
            onChange={(e) => setCustomOffset(parseInt(e.target.value) || null)}
            min="1"
            className="w-24"
          />
          <span className="text-sm text-muted-foreground">{t('form.fields.minutes')}</span>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleCustomOffset}
            disabled={!customOffset || customOffset <= 0}
          >
            {t('form.buttons.add')}
          </Button>
        </div>
        
        {/* Selected offsets */}
        {watchedOffsets && watchedOffsets.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {watchedOffsets.map((offset) => (
              <span
                key={offset}
                className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-primary/10 text-primary"
              >
                {formatOffset(offset)}
                <button
                  type="button"
                  onClick={() => handlePresetOffset(offset, false)}
                  className="ml-1 hover:text-destructive"
                >
                  ×
                </button>
              </span>
            ))}
          </div>
        )}
        
        {errors.offsets && (
          <p className="text-sm text-destructive">{errors.offsets.message}</p>
        )}
      </div>

      {/* Channels */}
      <div className="space-y-2">
        <Label>{t('form.fields.channels')}</Label>
        <div className="space-y-2">
          {[
            { key: 'in_app', label: t('form.channels.inApp') },
            { key: 'email', label: t('form.channels.email') },
          ].map((channel) => (
            <div key={channel.key} className="flex items-center space-x-2">
              <Checkbox
                id={`channel-${channel.key}`}
                checked={watchedChannels?.includes(channel.key as any) || false}
                onCheckedChange={(checked) => handleChannelChange(channel.key as any, checked as boolean)}
              />
              <Label htmlFor={`channel-${channel.key}`} className="text-sm">
                {channel.label}
              </Label>
            </div>
          ))}
        </div>
        {errors.channels && (
          <p className="text-sm text-destructive">{errors.channels.message}</p>
        )}
      </div>

      {/* Actions */}
      <div className="flex justify-end space-x-2 pt-4">
        <Button type="button" variant="outline" onClick={onCancel}>
          {t('common:cancel')}
        </Button>
        <Button type="submit" disabled={isSubmitting}>
          {isSubmitting 
            ? (isEdit ? t('common:updating') : t('common:creating'))
            : (isEdit ? t('actions.update') || 'Uložiť zmeny' : t('actions.create'))
          }
        </Button>
      </div>
    </form>
  );
}
